/**
 * Organisation (brand) settings: Keystone Glazing, Keystone South UK, …
 *
 * Each organisation is a brand with its own letterhead, wording, mailbox and numbering.
 * Every brand legally issues as the same company (legal_entity_name). Settings are edited in
 * Admin → Organisations; this module is the one place the app reads them from.
 *
 * Until the settings migration is applied the extra columns are missing, so everything here
 * falls back to the values that used to be hard-coded.
 */
import { directSupabase } from '../lib/supabaseClient';
import { ORGANIZATION_OPTIONS, setOrganizationEmailOverrides } from '../constants/organizations';

export type Brand = {
  id: string;
  code: string;
  name: string;
  tradingName: string;
  senderEmail: string | null;
  senderName: string | null;
  websiteUrl: string | null;
  portalLogoPath: string | null;
  legalEntityName: string | null;
  companyNumber: string | null;
  vatNumber: string | null;
  registeredOffice: string | null;
  isSelectable: boolean;
  isDefault: boolean;
  directoryOrgId: number | null;
  sortOrder: number;
  freeagentSyncEnabled: boolean;
};

export type BrandInput = Partial<Omit<Brand, 'id' | 'code'>>;

const LEGACY_SELECTABLE = new Set(['KSG']);
const LEGACY_DIRECTORY_IDS: Record<string, number> = { KSG: 4, KSP: 7 };

function text(value: unknown): string | null {
  const trimmed = String(value ?? '').trim();
  return trimmed ? trimmed : null;
}

export function fromDbBrand(row: Record<string, any>): Brand {
  const code = String(row.code || '').trim().toUpperCase();
  const hasSettings = 'is_selectable' in row;
  return {
    id: String(row.id),
    code,
    name: String(row.name || code),
    tradingName: text(row.trading_name) || String(row.name || code),
    senderEmail: text(row.sender_email),
    senderName: text(row.sender_name),
    websiteUrl: text(row.website_url),
    portalLogoPath: text(row.portal_logo_path),
    legalEntityName: text(row.legal_entity_name),
    companyNumber: text(row.company_number),
    vatNumber: text(row.vat_number),
    registeredOffice: text(row.registered_office),
    isSelectable: hasSettings ? !!row.is_selectable : LEGACY_SELECTABLE.has(code),
    isDefault: hasSettings ? !!row.is_default : code === 'KSG',
    directoryOrgId: row.directory_org_id != null ? Number(row.directory_org_id) : (LEGACY_DIRECTORY_IDS[code] ?? null),
    sortOrder: row.sort_order != null ? Number(row.sort_order) : 100,
    freeagentSyncEnabled: !!row.freeagent_sync_enabled,
  };
}

export function toDbBrand(input: BrandInput): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  const set = (key: string, value: unknown) => { if (value !== undefined) row[key] = value; };
  set('trading_name', input.tradingName !== undefined ? text(input.tradingName) : undefined);
  set('sender_email', input.senderEmail !== undefined ? text(input.senderEmail)?.toLowerCase() ?? null : undefined);
  set('sender_name', input.senderName !== undefined ? text(input.senderName) : undefined);
  set('website_url', input.websiteUrl !== undefined ? text(input.websiteUrl) : undefined);
  set('portal_logo_path', input.portalLogoPath !== undefined ? text(input.portalLogoPath) : undefined);
  set('legal_entity_name', input.legalEntityName !== undefined ? text(input.legalEntityName) : undefined);
  set('company_number', input.companyNumber !== undefined ? text(input.companyNumber) : undefined);
  set('vat_number', input.vatNumber !== undefined ? text(input.vatNumber) : undefined);
  set('registered_office', input.registeredOffice !== undefined ? text(input.registeredOffice) : undefined);
  set('is_selectable', input.isSelectable);
  set('directory_org_id', input.directoryOrgId);
  set('sort_order', input.sortOrder);
  set('freeagent_sync_enabled', input.freeagentSyncEnabled);
  return row;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateBrandInput(input: BrandInput): string | null {
  const email = text(input.senderEmail);
  if (email && !EMAIL_PATTERN.test(email)) return 'The sending mailbox does not look like an email address.';
  const site = text(input.websiteUrl);
  if (site && !/^https?:\/\//i.test(site)) return 'The website should start with https://';
  if (input.directoryOrgId != null && !(Number.isInteger(input.directoryOrgId) && input.directoryOrgId > 0)) {
    return 'The directory id must be a whole number.';
  }
  return null;
}

/** Brands in display order. */
export function sortBrands(brands: Brand[]): Brand[] {
  return [...brands].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

/** Brands that can be chosen for new work. Always includes `keep` (e.g. a record's current brand). */
export function selectableBrands(brands: Brand[], keep?: string | null): Brand[] {
  return sortBrands(brands).filter((brand) => brand.isSelectable || (keep != null && (brand.id === keep || brand.name === keep)));
}

/** The brand for new work when nothing else decides: the default, else Keystone Glazing, else the first. */
export function defaultBrand(brands: Brand[]): Brand | null {
  const sorted = sortBrands(brands);
  return sorted.find((brand) => brand.isDefault)
    || sorted.find((brand) => brand.code === 'KSG')
    || sorted.find((brand) => brand.isSelectable)
    || sorted[0]
    || null;
}

export function brandByName(brands: Brand[], name: string | null | undefined): Brand | null {
  const wanted = String(name || '').trim().toLowerCase();
  if (!wanted) return null;
  return brands.find((brand) => brand.name.toLowerCase() === wanted || brand.tradingName.toLowerCase() === wanted) || null;
}

export function brandByDirectoryId(brands: Brand[], directoryOrgId: number | null | undefined): Brand | null {
  if (directoryOrgId == null) return null;
  return brands.find((brand) => brand.directoryOrgId === Number(directoryOrgId)) || null;
}

/** Options for the customer directory's organisation field (customers.organization_id / name). */
export function directoryOptions(brands: Brand[], keepName?: string | null): Array<{ id: number; name: string }> {
  const options = selectableBrands(brands, keepName)
    .filter((brand) => brand.directoryOrgId != null)
    .map((brand) => ({ id: brand.directoryOrgId as number, name: brand.name }));
  return options.length ? options : ORGANIZATION_OPTIONS.map((option) => ({ ...option }));
}

/** Sending mailbox per brand name, for getOrganizationEmail. */
export function senderOverrides(brands: Brand[]): Record<string, { address: string; name: string }> {
  const out: Record<string, { address: string; name: string }> = {};
  for (const brand of brands) {
    if (!brand.senderEmail) continue;
    out[brand.name] = { address: brand.senderEmail, name: brand.senderName || brand.tradingName };
  }
  return out;
}

let cache: Promise<Brand[]> | null = null;

async function fetchBrands(): Promise<Brand[]> {
  const { data, error } = await directSupabase.from('organisations').select('*');
  if (error) throw new Error(error.message);
  const brands = sortBrands((data || []).map(fromDbBrand));
  setOrganizationEmailOverrides(senderOverrides(brands));
  return brands;
}

export const organisationSettings = {
  /** All brands, cached for the session. */
  list(force = false): Promise<Brand[]> {
    if (!cache || force) {
      cache = fetchBrands().catch((err) => {
        cache = null;
        throw err;
      });
    }
    return cache;
  },

  async update(id: string, input: BrandInput): Promise<Brand> {
    const problem = validateBrandInput(input);
    if (problem) throw new Error(problem);
    const { data, error } = await directSupabase
      .from('organisations')
      .update(toDbBrand(input))
      .eq('id', id)
      .select('*')
      .single();
    if (error) {
      if (/column|schema cache/i.test(error.message)) {
        throw new Error('Organisation settings are not set up in the database yet. Apply the organisation settings migration.');
      }
      if (error.code === '23505') throw new Error('That directory id is already used by another organisation.');
      throw new Error(error.message);
    }
    await this.list(true);
    return fromDbBrand(data);
  },

  /** Makes one brand the default for new work. */
  async setDefault(id: string): Promise<void> {
    const { error: clearError } = await directSupabase.from('organisations').update({ is_default: false }).eq('is_default', true);
    if (clearError) throw new Error(clearError.message);
    const { error } = await directSupabase.from('organisations').update({ is_default: true, is_selectable: true }).eq('id', id);
    if (error) throw new Error(error.message);
    await this.list(true);
  },

  async defaultBrand(): Promise<Brand | null> {
    return defaultBrand(await this.list());
  },
};

/** Loads brand settings at start-up so mailbox lookups use them. Safe to call more than once. */
export function primeOrganisationSettings(): void {
  void organisationSettings.list().catch(() => undefined);
}

/** The brand name of a workspace (projects.organisation_id), or null if it has none. */
export async function brandNameForProject(project?: { organisation_id?: string | null } | null): Promise<string | null> {
  const id = String((project as any)?.organisation_id || '').trim();
  if (!id) return null;
  try {
    const brands = await organisationSettings.list();
    return brands.find((brand) => brand.id === id)?.name || null;
  } catch {
    return null;
  }
}
